import { Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { PageHeader } from "@/components/PageHeader";
import { SearchInput } from "@/components/SearchInput";
import { ColumnPicker } from "@/components/ColumnPicker";
import {
  EquipmentPagination,
  EquipmentTable,
} from "@/components/equipment/EquipmentTable";
import { useEquipment } from "@/hooks/useEquipment";
import { useEquipmentListColumns } from "@/hooks/useEquipmentListColumns";
import { useEquipmentListParams } from "@/hooks/useEquipmentListParams";

/** Equipment without any tag, which the tag navigation can't reach. */
const EquipmentUntaggedPage = () => {
  const { search, page, setSearch, setPage } = useEquipmentListParams();
  const { data, isLoading, isFetching } = useEquipment({
    search,
    untagged: true,
    page,
  });

  const { columns, visibleColumns, setColumnVisible, resetColumns } =
    useEquipmentListColumns();

  return (
    <div className="space-y-6">
      <PageHeader title="Untagged" />

      <div className="space-y-2">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/equipment">Equipment</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage data-testid="equipment-untagged-title">
                Untagged
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <p className="text-sm text-muted-foreground">
          Equipment without a tag. Assign a tag so it shows up in its category.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search untagged equipment..."
          />
        </div>
        <ColumnPicker
          columns={columns}
          visibleColumns={visibleColumns}
          onColumnVisibleChange={setColumnVisible}
          onReset={resetColumns}
          testIdPrefix="equipment"
        />
      </div>

      <EquipmentTable
        items={data?.items ?? []}
        columns={visibleColumns}
        isLoading={isLoading}
        isFetching={isFetching}
      />
      <EquipmentPagination
        page={page}
        totalPages={data?.totalPages ?? 1}
        totalCount={data?.totalCount ?? 0}
        onPageChange={setPage}
      />
    </div>
  );
};

export default EquipmentUntaggedPage;
